import React, { useState, useEffect, useCallback } from "react";
import CustomButton from "../../../../../../components/Elements/Buttons/CustomButtons";
import { getApi, putApi } from "../../../../../../api/api_instance";
import { message } from "antd";
import toast from "react-hot-toast";
import { API_URLS } from "../../../../../../api/apiUrls";

export const QuestionForm = ({
  formId,
  sectionId,
  subSectionId,
  questions,
}) => {
  const [selectedQuestions, setSelectedQuestions] = useState({});
  const [previouslySelectedQuestions, setPreviouslySelectedQuestions] =
    useState({});
  const [loading, setLoading] = useState(false);

  const labelStyles = {
    color: "rgba(0, 0, 0, 0.88)",
    fontWeight: "400",
    marginBottom: "5px",
    fontSize: "13px",
    display: "block",
  };

  const commonStyles = {
    width: "100%",
    padding: "10px",
    marginTop: "8px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "300",
    backgroundColor: "#0087A31A",
    boxSizing: "border-box",
    outline: "none",
  };

  const fetchPreviouslySelected = useCallback(async () => {
    if (!subSectionId || !formId || !sectionId) return;

    setLoading(true);
    try {
      const response = await getApi({
        apiUrl: API_URLS.CUSTOMFORM_QUESTIONS(formId, sectionId, subSectionId),
      });

      if (response.success) {
        const customQuestions = response?.data?.questions || [];

        const preSelected = {};

        customQuestions.forEach((q) => {
          preSelected[q._id] = true;
        });

        setSelectedQuestions(preSelected);
        setPreviouslySelectedQuestions(preSelected);
      } else {
        setSelectedQuestions({});
        setPreviouslySelectedQuestions({});
      }
    } catch (error) {
      console.error("Error fetching selected questions:", error);
      toast.error("Failed to fetch selected questions");
    } finally {
      setLoading(false);
    }
  }, [formId, sectionId, subSectionId]);

  useEffect(() => {
    fetchPreviouslySelected();
  }, [fetchPreviouslySelected]);

  const handleSubmit = async () => {
    const addCustomQuestions = [];
    const deleteCustomQuestions = [];

    questions.forEach((question) => {
      const id = question._id;
      const section = question.customSectionId;
      const subsection = question.customSubsectionId;

      const isSelectedNow = !!selectedQuestions[id];
      const wasPreviouslySelected = !!previouslySelectedQuestions[id];

      if (isSelectedNow && !wasPreviouslySelected) {
        addCustomQuestions.push({
          section,
          subsection,
          questionId: id,
        });
      }

      if (!isSelectedNow && wasPreviouslySelected) {
        deleteCustomQuestions.push(id);
      }
    });

    const payload = {
      addCustomQuestions,
      deleteCustomQuestions,
    };

    try {
      const response = await putApi({
        apiUrl: API_URLS.SUBMIT_QUESTION(formId),
        data: payload,
      });

      if (response.success) {
        message.success(response.message || "Updated successfully");
        setPreviouslySelectedQuestions({ ...selectedQuestions });
      } else {
        message.error(response.message || "Update failed");
      }
    } catch (err) {
      message.error("Failed to update questions");
    }
  };

  const renderFieldByType = (question) => {
    const id = question._id;

    switch (question.type) {
      case "text":
      case "number":
      case "password":
      case "email":
      case "tel":
      case "url":
      case "search":
      case "date":
      case "time":
      case "week":
      case "month":
      case "datetime-local":
      case "color":
      case "range":
        return (
          <input
            id={id}
            type={question.type}
            placeholder="Please Enter"
            style={commonStyles}
          />
        );

      case "checkbox":
      case "radio":
        return (
          <label>
            <input type={question.type} />
            <span className="ml-2">{question.label}</span>
          </label>
        );

      case "multi-select":
        return (
          <>
            {question.options?.map((option) => (
              <label
                key={option._id}
                style={{ display: "block", marginBottom: "5px" }}
              >
                <input type="checkbox" value={option.value} />
                <span className="ml-2">{option.key}</span>
              </label>
            ))}
          </>
        );

      case "select":
        return (
          <select style={commonStyles}>
            <option value="">-- Select an option --</option>
            {question.options?.map((option) => (
              <option key={option._id} value={option.value}>
                {option.key}
              </option>
            ))}
          </select>
        );

      case "file":
        return <input type="file" style={{ marginTop: "8px" }} />;

      default:
        return (
          <p style={{ color: "red" }}>
            Unsupported field type: <strong>{question.type}</strong>
          </p>
        );
    }
  };

  if (!questions || questions.length === 0) {
    return <p>No questions to show.</p>;
  }

  return (
    <>
      <form>
        {questions.map((question) => {
          const id = question._id;

          return (
            <div
              key={id}
              className="flex items-start gap-2 mb-4 px-10 border bg-white border-[#3BA2B959] rounded-none mb-6 p-4"
            >
              <input
                type="checkbox"
                checked={!!selectedQuestions[id]}
                onChange={() =>
                  setSelectedQuestions((prev) => ({
                    ...prev,
                    [id]: !prev[id],
                  }))
                }
                className="mt-2"
              />
              <div className="w-full">
                {question.label && (
                  <label style={labelStyles}>{question.label}</label>
                )}
                {renderFieldByType(question)}
              </div>
            </div>
          );
        })}
      </form>

      <div className="flex justify-center gap-6 mt-8">
        <CustomButton
          buttonType="button"
          variant="cancel"
          buttonText="Cancel"
          onClick={() => setSelectedQuestions(previouslySelectedQuestions)}
          className="text-[16px] font-normal h-[50px] w-[200px]"
        />
        <CustomButton
          buttonType="button"
          variant="ok"
          buttonText="Save"
          onClick={handleSubmit}
          className="text-[16px] font-normal h-[50px] w-[200px]"
        />
      </div>
    </>
  );
};
